import { useMemo } from "react";
import { PresenceKind } from "@common/types/presence-nats";
import { useScope } from "@state/scopeContext.lib";
import { usePresenceViewers } from "./usePresenceViewers";
import { usePublishPresence } from "./usePublishPresence";

interface UseTaskPresenceParams {
    taskId: string | null | undefined;
    closeId: string | null | undefined;
}

/**
 * Announces that the current user is viewing the task; returns the other viewers.
 *
 * Besides the workspace heartbeat, a user working from an organization also
 * announces on the organization, which is what the organization-wide task list
 * and the Work tab observe.
 */
export const useTaskPresence = ({
    taskId,
    closeId,
}: UseTaskPresenceParams): number[] => {
    const { workspaceId, organizationId } = useScope();

    const workspaceTask = useMemo(
        () =>
            workspaceId && taskId
                ? { workspaceId, kind: PresenceKind.TASK, targetId: taskId }
                : null,
        [workspaceId, taskId],
    );
    const organizationTask = useMemo(
        () =>
            workspaceTask && organizationId
                ? { ...workspaceTask, organizationId }
                : null,
        [workspaceTask, organizationId],
    );
    const organizationWork = useMemo(
        () =>
            workspaceId && organizationId && closeId
                ? {
                      organizationId,
                      workspaceId,
                      kind: PresenceKind.WORK,
                      targetId: closeId,
                  }
                : null,
        [workspaceId, organizationId, closeId],
    );
    const viewedTask = useMemo(
        () =>
            workspaceTask && workspaceId
                ? {
                      scope: { workspaceId },
                      kind: workspaceTask.kind,
                      workspaceId,
                      targetId: workspaceTask.targetId,
                  }
                : null,
        [workspaceTask, workspaceId],
    );

    usePublishPresence(workspaceTask, { mode: "keepAlive" });
    usePublishPresence(organizationTask, { mode: "keepAlive" });
    usePublishPresence(organizationWork, { mode: "keepAlive" });

    return usePresenceViewers(viewedTask);
};
